import React from "react";
import { Button, Dropdown, Tooltip, Space } from "antd";
import type { MenuProps } from "antd";
import {
  SunOutlined,
  MoonOutlined,
  DesktopOutlined,
  BulbOutlined,
} from "@ant-design/icons";
import { useTheme, ThemeMode } from "../../contexts/ThemeContext";

interface ThemeToggleProps {
  size?: "small" | "middle" | "large";
  variant?: "simple" | "dropdown";
  showLabel?: boolean;
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  size = "middle",
  variant = "simple",
  showLabel = false,
}) => {
  const { theme, isDark, setTheme, toggleTheme } = useTheme();

  // Simple toggle button (just light/dark)
  if (variant === "simple") {
    return (
      <Tooltip title={`Switch to ${isDark ? "light" : "dark"} mode`}>
        <Button
          type="text"
          size={size}
          icon={
            isDark ? (
              <SunOutlined style={{ color: "#fadb14" }} />
            ) : (
              <MoonOutlined style={{ color: "#722ed1" }} />
            )
          }
          onClick={toggleTheme}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.3s ease",
          }}
          aria-label={`Switch to ${isDark ? "light" : "dark"} mode`}
        >
          {showLabel && (
            <span style={{ marginLeft: 4 }}>{isDark ? "Light" : "Dark"}</span>
          )}
        </Button>
      </Tooltip>
    );
  }

  // Dropdown with all options (light/dark/system)
  const getThemeIcon = (mode: ThemeMode) => {
    switch (mode) {
      case "light":
        return <SunOutlined style={{ color: "#fadb14" }} />;
      case "dark":
        return <MoonOutlined style={{ color: "#722ed1" }} />;
      case "system":
        return <DesktopOutlined style={{ color: "#1890ff" }} />;
    }
  };

  const getThemeLabel = (mode: ThemeMode) => {
    switch (mode) {
      case "light":
        return "Light";
      case "dark":
        return "Dark";
      case "system":
        return "System";
    }
  };

  const getCurrentIcon = () => {
    if (theme === "system") {
      return <DesktopOutlined style={{ color: "#1890ff" }} />;
    }
    return isDark ? (
      <MoonOutlined style={{ color: "#722ed1" }} />
    ) : (
      <SunOutlined style={{ color: "#fadb14" }} />
    );
  };

  const getCurrentLabel = () => {
    if (theme === "system") {
      return `System (${isDark ? "Dark" : "Light"})`;
    }
    return getThemeLabel(theme);
  };

  const menuItems: MenuProps["items"] = [
    {
      key: "light",
      icon: getThemeIcon("light"),
      label: "Light",
      onClick: () => setTheme("light"),
    },
    {
      key: "dark",
      icon: getThemeIcon("dark"),
      label: "Dark",
      onClick: () => setTheme("dark"),
    },
    {
      key: "system",
      icon: getThemeIcon("system"),
      label: "System",
      onClick: () => setTheme("system"),
    },
  ];

  return (
    <Dropdown
      menu={{ items: menuItems, selectedKeys: [theme] }}
      trigger={["click"]}
      placement="bottomRight"
    >
      <Button
        type="text"
        size={size}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          transition: "all 0.3s ease",
        }}
        aria-label="Theme selection"
      >
        <Space size={4}>
          {getCurrentIcon()}
          {showLabel && <span>{getCurrentLabel()}</span>}
        </Space>
      </Button>
    </Dropdown>
  );
};

// Simple icon-only theme toggle for compact spaces
export const ThemeToggleIcon: React.FC<{
  size?: "small" | "middle" | "large";
}> = ({ size = "middle" }) => {
  const { isDark, toggleTheme } = useTheme();

  return (
    <Tooltip title={`Switch to ${isDark ? "light" : "dark"} mode`}>
      <Button
        type="text"
        size={size}
        icon={
          <BulbOutlined
            style={{
              color: isDark ? "#fadb14" : "#722ed1",
              transform: isDark ? "rotate(0deg)" : "rotate(180deg)",
              transition: "all 0.3s ease",
            }}
          />
        }
        onClick={toggleTheme}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          aspectRatio: "1",
        }}
        aria-label={`Switch to ${isDark ? "light" : "dark"} mode`}
      />
    </Tooltip>
  );
};
